import React, { useState, useEffect } from 'react';

// Import Components
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import InvoiceCreator from './components/InvoiceCreator';
import SavedInvoices from './components/SavedInvoices';
import Footer from './components/Footer';
import SEOHead from './components/SEOHead';
import GoogleAnalytics, { trackEvent, trackPageView } from './components/GoogleAnalytics';

// Import Utils
import { saveInvoiceToMemory, showSaveNotification } from './utils/storage';

// Main App Component
const App = () => {
  const [currentView, setCurrentView] = useState('home');
  const [isSaved, setIsSaved] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [invoiceData, setInvoiceData] = useState({
    id: null,
    companyInfo: {
      name: '',
      address: '',
      email: '',
      phone: ''
    },
    clientInfo: {
      name: '',
      address: '',
      email: ''
    },
    invoiceDetails: {
      number: 'INV-001',
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      poNumber: ''
    },
    items: [{
      id: 1,
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0
    }],
    totals: {
      subtotal: 0,
      taxRate: 0,
      taxAmount: 0,
      total: 0
    },
    currency: 'USD',
    logo: null
  });

  // Track page view changes
  useEffect(() => {
    trackPageView(`/${currentView}`);
  }, [currentView]);

  // Check localStorage availability on app start
  useEffect(() => {
    const checkStorage = () => {
      try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
      } catch (e) {
        showSaveNotification('⚠️ Storage not available - data will not persist', 'error');
      }
    };
    
    checkStorage();
  }, []);

  // SEO Meta Data
  const getSEOData = () => {
    switch (currentView) {
      case 'create':
        return {
          title: 'Create Professional Invoice - Free Invoice Generator',
          description: 'Create professional invoices instantly with our free online invoice generator. Download as PDF, no registration required.'
        };
      case 'saved':
        return {
          title: 'Saved Invoices - Manage Your Invoices',
          description: 'View and manage all your saved invoices. Edit, download, or delete your invoice templates.'
        };
      default:
        return {
          title: 'Free Professional Invoice Generator - Create Invoices Online',
          description: 'Create stunning professional invoices in minutes. Free invoice generator with PDF download. No registration required, works offline.'
        };
    }
  };

  const createNewInvoice = () => {
    const invoiceNumber = `INV-${String(Date.now()).slice(-6)}`;
    setInvoiceData({
      id: null,
      companyInfo: { name: '', address: '', email: '', phone: '' },
      clientInfo: { name: '', address: '', email: '' },
      invoiceDetails: {
        number: invoiceNumber,
        date: new Date().toISOString().split('T')[0],
        dueDate: '',
        poNumber: ''
      },
      items: [{
        id: Date.now(),
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0
      }],
      totals: { subtotal: 0, taxRate: 0, taxAmount: 0, total: 0 },
      currency: 'USD',
      logo: null
    });
    setIsSaved(false);
    setCurrentView('create');
    
    // Track new invoice creation
    trackEvent('create_invoice', 'invoice', 'new_invoice');
  };

  const handleSaveInvoice = () => {
    try {
      const savedInvoice = saveInvoiceToMemory(invoiceData);
      setInvoiceData(savedInvoice);
      setIsSaved(true);
      setRefreshTrigger(prev => prev + 1);
      
      const message = invoiceData.id ? 'Invoice updated successfully!' : 'Invoice saved successfully!';
      showSaveNotification(message);
      
      // Track save event
      trackEvent('save_invoice', 'invoice', invoiceData.invoiceDetails.number);
    } catch (error) {
      console.error('Save error:', error);
      showSaveNotification('Failed to save invoice', 'error');
    }
  };

  const handleEditInvoice = (invoice) => {
    setInvoiceData(invoice);
    setIsSaved(true);
    setCurrentView('create');
    
    // Track edit event
    trackEvent('edit_invoice', 'invoice', invoice.invoiceDetails.number);
  };

  const handleGetStarted = () => {
    createNewInvoice();
    // Track get started click
    trackEvent('get_started', 'engagement', 'hero_button');
  };

  const seoData = getSEOData();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Google Analytics */}
      <GoogleAnalytics />
      
      {/* SEO Head */}
      <SEOHead 
        title={seoData.title}
        description={seoData.description}
        currentView={currentView}
      />
      
      {/* Navigation */}
      <Navbar 
        onNewInvoice={createNewInvoice}
        currentView={currentView}
        setCurrentView={setCurrentView}
      />
      
      {/* Main Content */}
      {currentView === 'home' && (
        <HeroSection onGetStarted={handleGetStarted} />
      )}
      
      {currentView === 'create' && (
        <InvoiceCreator 
          invoiceData={invoiceData}
          setInvoiceData={setInvoiceData}
          onSaveInvoice={handleSaveInvoice}
          isSaved={isSaved}
        />
      )}
      
      {currentView === 'saved' && (
        <SavedInvoices 
          onEditInvoice={handleEditInvoice} 
          refreshTrigger={refreshTrigger}
        />
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default App;